import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Phone, MessageSquare, MapPin, Clock, CalendarDays, Navigation, ExternalLink, FileDown } from "lucide-react";
import Footer from "@/components/Footer";
import { usePageMeta } from "@/seo";
import { nextBlackMountainDates, formatLong as fmt } from "@/data/blackMountainSchedule";
import { trackCall, trackText, trackBook, trackIntake } from "@/analytics";

const BLACK_MOUNTAIN_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Chiropractor",
  "@id": "https://kevingreenspan.com/black-mountain#location",
  name: "Dr. Kevin Greenspan",
  description: "Dr. Kevin Greenspan, DC sees patients on alternate Saturdays, 12:30–3:00 PM, in a rented treatment room at Mindful Wellness Space in Black Mountain, NC. Phone bookings only.",
  url: "https://kevingreenspan.com/black-mountain",
  telephone: "+18286913551",
  address: { "@type": "PostalAddress", streetAddress: "3174 US Hwy 70 W", addressLocality: "Black Mountain", addressRegion: "NC", postalCode: "28711", addressCountry: "US" },
  containedInPlace: { "@type": "Place", name: "Mindful Wellness Space", url: "https://www.mindfulwellness.space/" },
  paymentAccepted: "Cash, Check",
  founder: { "@type": "Person", name: "Kevin Greenspan", jobTitle: "Doctor of Chiropractic" },
};

const PLACE = "Mindful Wellness Space, 3174 US Hwy 70 W, Black Mountain, NC 28711";
const MAP_EMBED_URL = `https://www.google.com/maps?q=${encodeURIComponent(PLACE)}&z=15&output=embed`;
const DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(PLACE)}`;

export default function BlackMountain() {
  usePageMeta("/black-mountain");
  const [dates, setDates] = useState(() => nextBlackMountainDates(2));
  useEffect(() => setDates(nextBlackMountainDates(2)), []);

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="black-mountain-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(BLACK_MOUNTAIN_SCHEMA) }} />
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-[#E4DCCF]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="font-heading text-xl text-[#1A221E] tracking-tight">Dr. Greenspan</Link>
          <nav className="hidden sm:flex items-center gap-8">
            <Link to="/#about" className="font-body text-sm text-[#5C6A61] hover:text-[#1A221E] transition-colors">About</Link>
            <Link to="/#services" className="font-body text-sm text-[#5C6A61] hover:text-[#1A221E] transition-colors">Services</Link>
            <Link to="/blog" className="font-body text-sm text-[#5C6A61] hover:text-[#1A221E] transition-colors">Blog</Link>
            <Link to="/#contact" className="font-body text-sm text-[#5C6A61] hover:text-[#1A221E] transition-colors">Contact</Link>
          </nav>
          <a href="tel:+18286913551" onClick={trackCall("black_mountain")} className="font-body text-sm font-medium text-white bg-[#C87B5D] hover:bg-[#b56a4e] px-5 py-2 rounded-full transition-colors" data-testid="bm-header-call">
            Call (828) 691-3551
          </a>
        </div>
      </header>

      <section className="py-16 sm:py-24 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-5 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-3 space-y-8">
            <div>
              <p className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#C87B5D] mb-4">Location 2 · Black Mountain, NC</p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1A221E] tracking-tight leading-[1.05] mb-6" data-testid="bm-title">
                Saturday Chiropractic Care <br />in Black Mountain
              </h1>
              <p className="font-body text-base md:text-lg text-[#5C6A61] leading-relaxed">
                Every other Saturday afternoon, Dr. Kevin Greenspan sees patients at <strong className="text-[#1A221E]">Mindful Wellness Space</strong>, the holistic health center run by sisters Dr. Kirsten West, DC and Emily Horn on Highway 70 in Black Mountain. Dr. Greenspan's practice is fully independent — he simply rents a treatment room there — but the setting suits the whole-body approach he has practiced for more than forty years.
              </p>
            </div>

            <div className="space-y-6 font-body text-base text-[#5C6A61] leading-relaxed">
              <div>
                <h2 className="font-heading text-2xl text-[#1A221E] mb-2">Why Saturdays here</h2>
                <p>
                  Black Mountain afternoons are for people who can't get to Marion on a weekday — Swannanoa Valley locals, folks from east Asheville, and longtime patients who have moved this way. Appointments are arranged by phone or text so Dr. Greenspan can match each visit to the time you actually need.
                </p>
              </div>
              <div>
                <h2 className="font-heading text-2xl text-[#1A221E] mb-2">What to expect</h2>
                <p>
                  The same care as Marion — full spine adjusting, extremity work for shoulders, knees, feet and wrists, Applied Kinesiology muscle testing, Class IV K-Laser therapy, and practical nutritional guidance — in an unhurried Saturday setting. New patients are welcome. If you can, fill out the intake form before you arrive.
                </p>
              </div>
              <div>
                <h2 className="font-heading text-2xl text-[#1A221E] mb-2">About the space</h2>
                <p>
                  Mindful Wellness Space brings together chiropractic and functional medicine (Dr. West), acupuncture and Traditional Chinese Medicine (Pomai Nakoa, L.Ac.), and mindfulness meditation (Emily Horn). Their services are booked separately through{" "}
                  <a href="https://www.mindfulwellness.space/" target="_blank" rel="noopener noreferrer" className="text-[#3A4F41] underline underline-offset-4" data-testid="bm-mws-link">
                    mindfulwellness.space
                  </a>
                  . Dr. Greenspan's Saturday visits are booked only through him.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <a href="tel:+18286913551" onClick={trackCall("black_mountain")} className="inline-flex items-center gap-2 rounded-full bg-[#3A4F41] hover:bg-[#2d3e33] text-white font-body text-sm font-medium px-6 py-3 transition-colors" data-testid="bm-call-cta">
                <Phone size={16} /> Call (828) 691-3551
              </a>
              <a href="sms:+18286913551" onClick={trackText("black_mountain")} className="inline-flex items-center gap-2 rounded-full border border-[#3A4F41] text-[#3A4F41] hover:bg-[#3A4F41]/5 font-body text-sm font-medium px-6 py-3 transition-colors" data-testid="bm-text-cta">
                <MessageSquare size={16} /> Text to book
              </a>
              <a href="/intake-form.docx" download="New-Patient-Intake-Form.docx" onClick={trackIntake} className="inline-flex items-center gap-2 rounded-full border border-[#E4DCCF] text-[#5C6A61] hover:text-[#1A221E] font-body text-sm px-6 py-3 transition-colors" data-testid="bm-intake-link">
                <FileDown size={16} /> Intake form
              </a>
            </div>

            <p className="font-body text-sm text-[#5C6A61]" data-testid="bm-weekday-note">
              Need a weekday instead? Dr. Greenspan is in Marion on Tuesdays and Thursdays —{" "}
              <Link to="/book-online" onClick={trackBook} className="text-[#3A4F41] font-medium underline underline-offset-4">book online</Link>.
            </p>
          </div>

          <aside className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-3xl border border-[#E4DCCF] p-6 space-y-5" data-testid="bm-details-card">
              <div className="flex gap-3">
                <MapPin size={18} className="text-[#C87B5D] mt-0.5 flex-shrink-0" />
                <p className="font-body text-sm text-[#5C6A61] leading-relaxed">
                  <strong className="text-[#1A221E]">Mindful Wellness Space</strong><br />
                  3174 US Hwy 70 W<br />Black Mountain, NC 28711
                </p>
              </div>
              <div className="flex gap-3">
                <Clock size={18} className="text-[#C87B5D] mt-0.5 flex-shrink-0" />
                <p className="font-body text-sm text-[#5C6A61] leading-relaxed">
                  <strong className="text-[#1A221E]">Alternate Saturdays</strong><br />12:30 PM – 3:00 PM
                </p>
              </div>
              <div className="flex gap-3" data-testid="bm-next-dates">
                <CalendarDays size={18} className="text-[#C87B5D] mt-0.5 flex-shrink-0" />
                <p className="font-body text-sm text-[#5C6A61] leading-relaxed">
                  <strong className="text-[#1A221E]">Next Saturdays</strong><br />
                  {dates.map((d) => <span key={d.toISOString()} className="block" suppressHydrationWarning>{fmt(d)}</span>)}
                </p>
              </div>
              <div className="rounded-2xl bg-[#C87B5D]/10 border border-[#C87B5D]/30 px-4 py-3 font-body text-sm text-[#1A221E]" data-testid="bm-phone-only-notice">
                <strong>Phone bookings only.</strong> This location is not bookable online —{" "}
                <a href="tel:+18286913551" onClick={trackCall("black_mountain")} className="text-[#C87B5D] font-medium hover:underline">call</a> or{" "}
                <a href="sms:+18286913551" onClick={trackText("black_mountain")} className="text-[#C87B5D] font-medium hover:underline">text (828) 691-3551</a>.
              </div>
            </div>

            <div className="rounded-3xl overflow-hidden border border-[#E4DCCF] bg-white" data-testid="bm-map">
              <iframe
                src={MAP_EMBED_URL}
                title="Map to Mindful Wellness Space, 3174 US Hwy 70 W, Black Mountain, NC"
                width="100%"
                height="240"
                style={{ border: 0, display: "block" }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
              <a href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-3 px-5 py-3 font-body text-sm text-[#3A4F41] hover:bg-[#F7F5F0] transition-colors" data-testid="bm-directions-link">
                <span className="flex items-center gap-2"><Navigation size={14} className="text-[#C87B5D]" /> Get Directions</span>
                <ExternalLink size={12} className="text-[#C87B5D]" />
              </a>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </div>
  );
}
